import React from 'react';
import { useFees } from '../../context/FeeContext';

const AcceptQuoteModal = ({
  booking,
  quoteAmount,
  quoteNotes,
  projectEndDate,
  projectEndTime,
  blockCalendar,
  submitting,
  onQuoteAmountChange,
  onQuoteNotesChange,
  onProjectEndDateChange,
  onProjectEndTimeChange,
  onBlockCalendarChange,
  onSubmit,
  onClose,
  calculateFees
}) => {
  const { feeRates, calculateProviderPayout } = useFees();
  
  if (!booking) return null;
  
  // Calculate fees using dynamic rates
  const amount = parseFloat(quoteAmount) || 0;
  const providerCalc = calculateProviderPayout(amount);

  // Check if this is a project-based booking
  const isProjectBooking = booking.price_unit === 'project';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="accept-quote-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Accept Booking</h2>
        
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-800">{booking.service_title}</h3>
            {/* Booking Order Code for service bookings */}
            {booking.booking_order_code && (
              <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-2 py-0.5 rounded-full font-mono font-medium" title="Service Booking Code">
                #SE-{booking.booking_order_code}
              </span>
            )}
            {/* Job Order Code for job-based bookings */}
            {booking.type === 'job' && booking.job_order_code && (
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs px-2 py-0.5 rounded-full font-mono font-medium" title="Job Order Code">
                #JO-{booking.job_order_code}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">Client: {booking.client_name}</p>
          {booking.scheduled_date && (
            <p className="text-sm text-gray-600">
              {isProjectBooking ? 'Start Date:' : 'Date:'} {booking.scheduled_date} {booking.scheduled_time}
            </p>
          )}
          <p className="text-sm text-gray-600">Original Price: ₱{booking.price?.toLocaleString()}</p>
          {isProjectBooking && (
            <p className="text-xs text-blue-600 mt-1 font-medium">📋 Project-based booking</p>
          )}
        </div>

        {/* Project End Date Section - Only for project bookings */}
        {isProjectBooking && (
          <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Project Timeline
            </h4>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-purple-700 mb-1">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={projectEndDate || ''}
                  onChange={(e) => onProjectEndDateChange(e.target.value)}
                  min={booking.scheduled_date || new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  data-testid="project-end-date-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-purple-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={projectEndTime || '17:00'}
                  onChange={(e) => onProjectEndTimeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  data-testid="project-end-time-input"
                />
              </div>
            </div>

            {/* Block Calendar Checkbox */}
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-purple-200">
              <input
                type="checkbox"
                id="blockCalendar"
                checked={blockCalendar || false}
                onChange={(e) => onBlockCalendarChange(e.target.checked)}
                className="mt-0.5 h-5 w-5 text-purple-600 border-purple-300 rounded focus:ring-purple-500"
                data-testid="block-calendar-checkbox"
              />
              <div>
                <label htmlFor="blockCalendar" className="text-sm font-medium text-purple-800 cursor-pointer">
                  Block my calendar during this project
                </label>
                <p className="text-xs text-purple-600 mt-1">
                  When enabled, other clients won't be able to book you from {booking.scheduled_date} to the end date.
                  Leave unchecked if you can handle multiple projects simultaneously.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">
            Final Service Amount (₱) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={quoteAmount}
            onChange={(e) => onQuoteAmountChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
            placeholder="Enter final amount"
            min="1"
            step="0.01"
            data-testid="quote-amount-input"
          />
        </div>

        {/* Auto-calculated fees */}
        {quoteAmount && parseFloat(quoteAmount) > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
            <h4 className="font-semibold text-gray-700 mb-2">Fee Breakdown:</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Final Amount:</span>
                <span className="font-semibold">₱{providerCalc.serviceAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>Platform Fee ({feeRates.provider_fee_rate}%):</span>
                <span>- ₱{providerCalc.providerFee.toLocaleString()}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between text-green-600 font-bold">
                <span>Your Payout:</span>
                <span>₱{providerCalc.providerPayout.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Notes for Client (Optional)</label>
          <textarea
            value={quoteNotes}
            onChange={(e) => onQuoteNotesChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
            placeholder="Any additional notes about the service or pricing..."
            data-testid="quote-notes-input"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
            data-testid="cancel-quote-btn"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting || !quoteAmount || parseFloat(quoteAmount) <= 0 || (isProjectBooking && !projectEndDate)}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
            data-testid="submit-quote-btn"
          >
            {submitting ? 'Accepting...' : 'Accept Booking'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptQuoteModal;
